import { useQuery } from '@apollo/react-hooks';
import { Box, Heading2, StackView, Text } from 'localmed-core';
import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { DiButton, Link, RadioGroup } from '../../../shared/components/DiComponents';
import { DiGenericLoading } from '../../../shared/components/DiLayout';
import { LM_CLIENT_CONTEXT } from '../../../shared/constants';
import { BackLink } from '../../pages/SchedulingPage/components';
import { IStepProps } from '../../pages/SchedulingPage/components/schedulingWizard';
import { BackButton, ContentWrapper, Wrapper } from '../../pages/SchedulingPage/components/styles';
import { shouldSkipReasonSelectionScreen } from '../../pages/SchedulingPage/utils';
import SelectedReasonForVisit from '../SelectedReasonForVisit';
import query from './ProvidersListQuery.graphql';
import SchedulingProviderCard from './SchedulingProviderCard';

interface Props extends IStepProps {}

export default function SchedulingProviderSelectionScreen({ wizard }: Props) {
  const { reasonForVisit, office, provider, initialSelectedDate } = wizard?.values || {};
  const previousProviderRef = useRef(provider);
  const { data, loading } = useQuery(query, {
    variables: { officeId: office?.id, reasonForVisitId: reasonForVisit?.id },
    skip: office == null,
    context: LM_CLIENT_CONTEXT,
  });
  useEffect(() => {
    if (wizard?.trackSchedulingEvent) {
      wizard.trackSchedulingEvent('Provider Selection Screen', 'SchedulingProviderSelectionScreen');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <DiGenericLoading />;
  if (!data) return null;
  if (!wizard) return null;

  const { nodes } = data.providers;
  function goBack() {
    if (wizard == null) return;

    wizard.form.batch(() => {
      wizard.form.change('provider', previousProviderRef.current);
      wizard.form.change('queryProvider', previousProviderRef.current);
    });

    if (shouldSkipReasonSelectionScreen(wizard)) {
      wizard.gotoStep(0);
    } else {
      wizard.gotoPreviousStep();
    }
  }
  return (
    <Wrapper>
      <BackLink onClick={goBack} />
      <ContentWrapper>
        <Box textAlign={{ _: 'center', lg: 'left' }} width="100%">
          <StackView spacing="xl">
            <SelectedReasonForVisit wizard={wizard} />
            <StackView spacing="md">
              <Heading2>
                <FormattedMessage
                  id="scheduling.SchedulingProviderSelectionScreen.title"
                  defaultMessage="Which provider would you like to see?"
                />
              </Heading2>
              <Text>
                <FormattedMessage
                  id="scheduling.SchedulingProviderSelectionScreen.providerSelectCaption"
                  defaultMessage="Select one:"
                />
              </Text>
              <RadioGroup
                name="provider"
                items={nodes}
                itemDisplayComponent={SchedulingProviderCard}
                value={provider}
                columns={2}
                onChange={(value) => {
                  wizard.form.batch(() => {
                    wizard.form.change('provider', value);
                    wizard.form.change('queryProvider', value);
                    wizard.form.change('selectedDate', initialSelectedDate);
                  });
                }}
              />
            </StackView>
          </StackView>
          <Link
            onClick={() => {
              wizard.form.batch(() => {
                wizard.form.change('provider', null);
                wizard.form.change('queryProvider', null);
              });
              wizard.gotoNextStep();
            }}
          >
            <FormattedMessage
              id="scheduling.SchedulingProviderSelectionScreen.viewAllProviderOpeningsMessage"
              defaultMessage="View All Provider Openings"
            />
          </Link>
        </Box>
      </ContentWrapper>
      <Box display="flex" justifyContent={{ _: 'center', lg: 'space-between' }}>
        <BackButton
          onClick={goBack}
          display={{ _: 'none', lg: 'inherit' }}
          variant="subtle"
          size="xl"
        >
          <FormattedMessage
            id="scheduling.SchedulingProviderSelectionScreen.cancelButton"
            defaultMessage="Back"
          />
        </BackButton>
        <DiButton
          onClick={() => {
            wizard.gotoNextStep();
          }}
          size="xl"
          disabled={!provider}
        >
          <FormattedMessage
            id="scheduling.SchedulingProviderSelectionScreen.nextButton"
            defaultMessage="Next"
          />
        </DiButton>
      </Box>
    </Wrapper>
  );
}

SchedulingProviderSelectionScreen.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  id: 'SCHEDULING_PROVIDER_SELECTION_SCREEN',
};
